import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useEffect, useRef } from 'react';
import CalendarOption from '../CalendarOption';
import {
  checkTokenExists,
  checkTokenExistsSuccess,
  getGoogleCalendarAuthURI,
  getOutlookCalendarAuthURI,
  resetCalendarAuthURI,
  resetCheckTokenExists,
  resetDeleteCalendar,
  resetSaveLinkedCalendar,
  saveLinkedCalendar,
} from '../../../redux/booking/bookingSlice';
import { useAppSelector } from '../../../redux/hooks';
import { TokenTypeEnum } from '../../../types/booking';

const calendarOptions = [
  {
    id: 1,
    icon: '/assets/icons/logos-google-calendar.svg',
    disableIcon: '/assets/icons/google-calendar-faded.svg',
    text: 'bookings.linkCalendarStep.googleCalendar',
  },
  {
    id: 2,
    icon: '/assets/icons/file-type-outlook.svg',
    disableIcon: '/assets/icons/file-type-outlook-disable.svg',
    text: 'bookings.linkCalendarStep.outlookCalendar',
  },
];

type ILinkCalendarProps = {
  setSelectedOption: (_optionId: number | null) => void;
  selectedOption: number | null;
  hasPermissions: boolean;
};

function LinkCalendarStep({
  setSelectedOption,
  selectedOption,
  hasPermissions,
}: ILinkCalendarProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    googleCalendarAuthURI,
    outlookCalendarAuthURI,
    checkTokenExistsLoading,
    coachConfigurations,
    deleteCalendarLoading,
    deleteCalendarSuccess,
    coachConfigurationsLoading,
    googleCalendarAuthURIFailure,
    outlookCalendarAuthURIFailure,
    checkTokenExistsFailure,
    username,
    googleCalendarAuthURISuccess,
    outlookCalendarAuthURISuccess,
    isTokenExists,
    linkedCalendarSuccess,
    linkedCalendarLoading,
  } = useAppSelector((state) => state.booking);
  const coachId = useAppSelector((state) => state.coach.coach.id);
  const authWindowRef = useRef<Window | null>(null);

  useEffect(() => {
    if (googleCalendarAuthURIFailure || outlookCalendarAuthURIFailure || checkTokenExistsFailure) {
      setSelectedOption(null);
    }
  }, [googleCalendarAuthURIFailure, outlookCalendarAuthURIFailure, checkTokenExistsFailure]);

  useEffect(() => {
    if (googleCalendarAuthURI && !authWindowRef.current) {
      authWindowRef.current = window.open(
        googleCalendarAuthURI,
        '_blank',
        'location=yes,height=570,width=520,scrollbars=yes,status=yes'
      );

      const checkWindowClosed = setInterval(() => {
        if (authWindowRef.current?.closed) {
          clearInterval(checkWindowClosed);
          authWindowRef.current = null;
          dispatch(checkTokenExists({ coachId, tokenType: TokenTypeEnum.GOOGLE_CALENDER }));
        }
      }, 1000);

      return () => {
        clearInterval(checkWindowClosed);
        if (authWindowRef.current) {
          authWindowRef.current.close();
          authWindowRef.current = null;
        }
      };
    }
  }, [googleCalendarAuthURI]);

  useEffect(() => {
    if (outlookCalendarAuthURI && !authWindowRef.current) {
      authWindowRef.current = window.open(
        outlookCalendarAuthURI,
        '_blank',
        'location=yes,height=570,width=520,scrollbars=yes,status=yes'
      );

      const checkWindowClosed = setInterval(() => {
        if (authWindowRef.current?.closed) {
          clearInterval(checkWindowClosed);
          authWindowRef.current = null;
          dispatch(checkTokenExists({ coachId, tokenType: TokenTypeEnum.MICROSOFT_CALENDAR }));
        }
      }, 1000);

      return () => {
        clearInterval(checkWindowClosed);
        if (authWindowRef.current) {
          authWindowRef.current.close();
          authWindowRef.current = null;
        }
      };
    }
  }, [outlookCalendarAuthURI]);

  useEffect(() => {
    if (coachConfigurations.calendar === TokenTypeEnum.GOOGLE_CALENDER) {
      setSelectedOption(1);
      dispatch(checkTokenExistsSuccess({ isTokenExist: true }));
    }
    if (coachConfigurations.calendar === TokenTypeEnum.MICROSOFT_CALENDAR) {
      setSelectedOption(2);
      dispatch(checkTokenExistsSuccess({ isTokenExist: true }));
    }
  }, [coachConfigurations.calendar]);

  const handleSelectOption = (optionId: number | null) => {
    setSelectedOption(optionId);
    if (optionId === 1) {
      dispatch(
        getGoogleCalendarAuthURI({
          coachId,
          videoCallPlatform: coachConfigurations.videoCallPlatform,
          videoCallPlatformUsername: coachConfigurations.videoCallPlatformUsername,
          activeStep: 1,
        })
      );
    }
    if (optionId === 2) {
      dispatch(getOutlookCalendarAuthURI());
    }
    dispatch(resetDeleteCalendar());
    dispatch(resetSaveLinkedCalendar());
  };

  useEffect(() => {
    if (linkedCalendarSuccess) {
      dispatch(resetSaveLinkedCalendar());
      dispatch(resetCalendarAuthURI());
    }
  }, [linkedCalendarSuccess]);

  useEffect(() => {
    if (googleCalendarAuthURISuccess && isTokenExists && username) {
      if (selectedOption === 1) {
        dispatch(
          saveLinkedCalendar({
            coachId,
            calendar: TokenTypeEnum.GOOGLE_CALENDER,
            calendarUsername: username,
            activeStep: 1,
          })
        );
      }
    }
    if (outlookCalendarAuthURISuccess && isTokenExists && username) {
      if (selectedOption === 2) {
        dispatch(
          saveLinkedCalendar({
            coachId,
            calendar: TokenTypeEnum.MICROSOFT_CALENDAR,
            calendarUsername: username,
            activeStep: 1,
          })
        );
      }
    }
  }, [googleCalendarAuthURISuccess, outlookCalendarAuthURISuccess, isTokenExists, username]);

  useEffect(
    () => () => {
      dispatch(resetCalendarAuthURI());
      dispatch(resetSaveLinkedCalendar());
      dispatch(resetCheckTokenExists());
      setSelectedOption(null);
    },
    [dispatch]
  );

  return (
    <Grid display="flex" flexDirection="column" alignItems="center" height="calc(100% - 68px)">
      <Grid gap={6} display="flex" flexDirection="column" alignItems="center">
        <Typography variant="title1Bold" color="primary">
          {t('bookings.linkCalendarStep.title')}
        </Typography>
        <Typography variant="bodyRegular" color="primary" maxWidth={542} textAlign="center">
          {t('bookings.linkCalendarStep.subTitle')}
        </Typography>
      </Grid>
      <Grid
        display="flex"
        justifyContent="center"
        m="auto"
        gap={2}
        width="100%"
        flexDirection="column"
        alignItems="center"
      >
        {calendarOptions.map((option) => (
          <CalendarOption
            key={option.id}
            id={option.id}
            icon={option.icon}
            disableIcon={option.disableIcon}
            text={option.text}
            selectedOption={selectedOption}
            onSelect={handleSelectOption}
            loading={checkTokenExistsLoading || coachConfigurationsLoading || linkedCalendarLoading}
            deleteLoading={deleteCalendarLoading}
            deleteSuccess={deleteCalendarSuccess}
            email={username || coachConfigurations.calendarUsername}
            isCalendar
            hasPermissions={hasPermissions}
          />
        ))}
      </Grid>
    </Grid>
  );
}

export default LinkCalendarStep;
